/*
  Carnevore Mountain: adding to the cart from a page (shop cards and the product page).

  Every add goes through the cart drawer (drawer.addItems, in assets/cart-drawer.js), which runs it inside the
  drawer's busy state, checkout lock and stale-response guard, repaints the drawer, header count and free delivery
  bar, resolves Shopify's cart-lines event with the whole cart, and announces the change. Then drawer.open(button)
  opens the drawer, and focus returns to that button when it closes.

  - window.cmCart.add(items, { message }): queues an add. One add runs at a time, in the order tapped: each waits
    for the drawer to finish any change it is making (pendingRequests), and an add the drawer turns away while busy
    is tried again. It is refused while checkout is under way. Returns a promise; a refused add (sold out, stock)
    rejects with Shopify's message.
  - <cm-quick-add>: the product card's form (snippets/cm-product-card.liquid). With the drawer, it adds through
    window.cmCart and opens the drawer; without it (cart type "Page"), the form posts to /cart/add as usual.
    A size choice updates the price shown.
*/
(() => {
  if (window.cmCart) return;

  let queue = Promise.resolve();
  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };
  // The drawer refuses a change while another one runs; wait for it to finish.
  const whenIdle = (drawer) =>
    new Promise((resolve) => {
      const check = () => (drawer.pendingRequests > 0 ? setTimeout(check, 40) : resolve());
      check();
    });
  const drawerFor = () => {
    const drawer = document.querySelector('cart-drawer');
    return drawer && typeof drawer.addItems === 'function' ? drawer : null;
  };
  const attempt = (drawer, items, options) =>
    whenIdle(drawer).then(() => {
      if (drawer.checkingOut) throw new Error('');
      return drawer.addItems(items, options) || attempt(drawer, items, options);
    });

  window.cmCart = {
    // The drawer, or null when the cart is a page (then forms post as usual).
    drawer: drawerFor,
    add(items, options = {}) {
      const drawer = drawerFor();
      if (!drawer) return Promise.reject(new Error('No cart drawer'));
      return enqueue(() => attempt(drawer, items, options));
    },
  };

  class CmQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('form');
      this.button = this.querySelector('.cm-pcard__add');
      this.error = this.querySelector('.cm-pcard__error');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.addEventListener('change', (event) => this.onSizeChange(event));
    }

    // A size choice updates the price shown.
    onSizeChange(event) {
      const input = event.target;
      if (input.name !== 'id' || input.type !== 'radio') return;
      const price = this.querySelector('[data-cm-price]');
      if (price && input.dataset.price) price.textContent = input.dataset.price;
      const compare = this.querySelector('[data-cm-compare]');
      if (compare) compare.textContent = input.dataset.compare || '';
      const unit = this.querySelector('[data-cm-unit]');
      if (unit) unit.textContent = input.dataset.unit || '';
      this.showError('');
    }

    onSubmit(event) {
      const drawer = drawerFor();
      if (!drawer) return; // cart type "Page": post the form as usual
      event.preventDefault();
      if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;
      const data = new FormData(this.form);
      const id = Number(data.get('id'));
      if (!id) return;
      const quantity = Number(data.get('quantity')) || 1;
      const title = (this.closest('.cm-pcard')?.querySelector('.cm-pcard__link')?.textContent || '').trim();

      this.setBusy(true);
      this.showError('');
      window.cmCart
        .add([{ id, quantity }], { message: title ? `Added ${title} to your cart.` : '' })
        .then(() => drawer.open(this.button))
        .catch((error) => this.showError(error.message || (window.cartStrings && window.cartStrings.error) || ''))
        .finally(() => this.setBusy(false));
    }

    setBusy(busy) {
      this.button.classList.toggle('is-loading', busy);
      if (busy) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');
      const label = this.button.querySelector('.cm-pcard__add-label');
      if (label) {
        label.textContent = busy ? this.button.dataset.adding : this.button.dataset.add;
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }
  if (!customElements.get('cm-quick-add')) customElements.define('cm-quick-add', CmQuickAdd);
})();
